import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/customer/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/signin"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div data-testid="reset-invalid" className="max-w-md mx-auto px-4 py-24 text-center">
        <h1 className="font-[Outfit] text-2xl font-black text-red-400">Invalid reset link</h1>
        <p className="mt-2 text-slate-400 text-sm">This URL is missing a reset token.</p>
        <Link to="/forgot-password" className="mt-6 inline-block text-yellow-400 hover:text-yellow-300 font-mono text-sm uppercase">
          Request a new link →
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div data-testid="reset-success" className="max-w-md mx-auto px-4 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-yellow-400 mx-auto" />
        <h1 className="mt-6 font-[Outfit] text-3xl font-black">Password updated!</h1>
        <p className="mt-4 text-slate-400">Redirecting you to sign in…</p>
      </div>
    );
  }

  return (
    <div data-testid="reset-page" className="min-h-[70vh] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-yellow-400 text-slate-950 mb-4">
          <KeyRound className="w-6 h-6" />
        </div>
        <h1 className="font-[Outfit] text-3xl font-black tracking-tight">Set new password</h1>
        <p className="text-sm text-slate-400 mt-1">Choose a strong one (min 6 characters).</p>

        <div className="mt-8 space-y-4">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5 block">New password</span>
            <input
              data-testid="reset-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-yellow-400"
            />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5 block">Confirm password</span>
            <input
              data-testid="reset-confirm"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-yellow-400"
            />
          </label>
        </div>

        {error && (
          <div data-testid="reset-error" className="mt-4 text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded text-sm">{error}</div>
        )}

        <button
          data-testid="reset-submit"
          type="submit"
          disabled={submitting}
          className="mt-6 w-full bg-yellow-400 text-slate-950 font-bold px-4 py-3 rounded-md uppercase tracking-wide hover:bg-yellow-300 disabled:opacity-50"
        >
          {submitting ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
